import React from "react";
import { AuditOutlined, BellFilled, ClockCircleOutlined, DeleteOutlined, DollarOutlined, EditOutlined, ExclamationCircleOutlined, FileDoneOutlined, FileSearchOutlined, FireFilled, SettingFilled } from "@ant-design/icons";
import { LostStatuses } from "../../../types/LostStatuses";
import { IconWrapper } from "./styles";
import { ReportType } from "../../../redux/features/board/columnTypes";
import { commonLabels } from "../../../utils/constants/labels";
import { getStatusTheme } from "./statusThemes";

type IconComponentType = React.ComponentType<{ style?: React.CSSProperties }>;
type StatusValue = (typeof LostStatuses)[keyof typeof LostStatuses];

const createStatusIcon = (status: StatusValue, IconComponent: IconComponentType) => {
  const theme = getStatusTheme(status);
  return (
    <IconWrapper borderColor={theme.border} backgroundColor={theme.background}>
      <IconComponent style={{ color: theme.pillColor }} />
    </IconWrapper>
  );
};

export const columnConfig = [
  {
    status: LostStatuses.Urgent,
    title: getStatusTheme(LostStatuses.Urgent).label || commonLabels.urgent,
    icon: createStatusIcon(LostStatuses.Urgent, FireFilled),
    isOnBoard: true,
    type: ReportType.Lost,
  },
  {
    status: LostStatuses.DraftIncomplete,
    title: getStatusTheme(LostStatuses.DraftIncomplete).label || commonLabels.draftIncomplete,
    icon: createStatusIcon(LostStatuses.DraftIncomplete, EditOutlined),
    isOnBoard: false,
    type: ReportType.Lost,
  },
  {
    status: LostStatuses.DraftCompleted,
    title: getStatusTheme(LostStatuses.DraftCompleted).label || commonLabels.draftCompleted,
    icon: createStatusIcon(LostStatuses.DraftCompleted, FileDoneOutlined),
    isOnBoard: false,
    type: ReportType.Lost,
  },
  {
    status: LostStatuses.NotPaid,
    title: getStatusTheme(LostStatuses.NotPaid).label || commonLabels.notPaid,
    icon: createStatusIcon(LostStatuses.NotPaid, DollarOutlined),
    isOnBoard: false,
    type: ReportType.Lost,
  },
  {
    status: LostStatuses.Open,
    title: getStatusTheme(LostStatuses.Open).label || commonLabels.columnOpen,
    icon: createStatusIcon(LostStatuses.Open, BellFilled),
    isOnBoard: true,
    type: ReportType.Lost,
  },
  {
    status: LostStatuses.Pending,
    title: getStatusTheme(LostStatuses.Pending).label || commonLabels.columnPending,
    icon: createStatusIcon(LostStatuses.Pending, ClockCircleOutlined),
    isOnBoard: false,
    type: ReportType.Lost,
  },
  {
    status: LostStatuses.Review,
    title: getStatusTheme(LostStatuses.Review).label || commonLabels.columnReview,
    icon: createStatusIcon(LostStatuses.Review, AuditOutlined),
    isOnBoard: true,
    type: ReportType.Lost,
  },
  {
    // Restored to the main kanban — Review → InProcess auto-promotion (after the
    // recentPaymentHighlightHours window expires post-payment) populates this
    // column with actionable claims. Sits between Review (locked / hold-off)
    // and LFNotified (admin has contacted the L&F department) in the flow.
    status: LostStatuses.InProcess,
    title: getStatusTheme(LostStatuses.InProcess).label || commonLabels.inProcess,
    icon: createStatusIcon(LostStatuses.InProcess, SettingFilled),
    isOnBoard: true,
    type: ReportType.Lost,
  },
  {
    status: LostStatuses.LFNotified,
    title: getStatusTheme(LostStatuses.LFNotified).label || commonLabels.lFNotified,
    icon: createStatusIcon(LostStatuses.LFNotified, FileSearchOutlined),
    isOnBoard: true,
    type: ReportType.Lost,
  },
  {
    status: LostStatuses.Overdue,
    title: getStatusTheme(LostStatuses.Overdue).label || commonLabels.overdue,
    icon: createStatusIcon(LostStatuses.Overdue, ExclamationCircleOutlined),
    isOnBoard: false,
    type: ReportType.Lost,
  },
  {
    status: LostStatuses.Deleted,
    title: getStatusTheme(LostStatuses.Deleted).label || commonLabels.deleted,
    icon: createStatusIcon(LostStatuses.Deleted, DeleteOutlined),
    isOnBoard: true,
    type: ReportType.Lost,
  },
];
export const columnOrderConfig = {
  [`${LostStatuses.Urgent}-${ReportType.Lost}`]: [
    { "payment_agg.successful_payment_count": "DESC" },
    { "payment_agg.payment_date": "DESC" },
    { "sticky_agg.enabled_count": "DESC" },
    { "sticky_agg.created_at": "ASC" },
    // {
    //   "CASE WHEN pending_request_agg.request_type =  :updateBillingInfoAction THEN 1 WHEN pending_request_agg.request_type = :updateAction THEN 2 WHEN pending_request_agg.request_type = :deleteAction THEN 3 ELSE 4 END":
    //     "ASC",
    // },
    { "lost.createdAt": "DESC" },
  ],
  [`${LostStatuses.Pending}-${ReportType.Lost}`]: [{ "lost.createdAt": "DESC" }],
  // Order by oldest payment first so the first paginated page surfaces the
  // longest-waiting (actionable, past-refund-window) cards. Previously sorted by
  // `lost.createdAt DESC`, which meant page 1 was dominated by the most recently
  // created claims — the same ones that are still inside the refund window — so the
  // bulk of green cards only appeared after clicking "Load more". Tiebreak on
  // createdAt DESC for stable ordering when multiple claims share a payment_date.
  [`${LostStatuses.Review}-${ReportType.Lost}`]: [{ "payment_agg.payment_date": "ASC" }, { "lost.createdAt": "DESC" }],
  [`${LostStatuses.Open}-${ReportType.Lost}`]: [{ "sticky_agg.enabled_count": "DESC" }, { "sticky_agg.created_at": "ASC" }, { "lost.createdAt": "DESC" }],
  // In Process: "Mark as Urgent" (lost.isUrgent flag — the 🔥 fire icon on cards)
  // is the absolute top-priority signal. Below the urgent bucket, claims sort by
  // EARLIEST paid first — the longest-waiting actionable claim works the queue FIFO.
  // Overdue is a card-level visual signal, not an ordering signal: an overdue claim
  // that was paid earlier still sits above a non-overdue one with a later payment,
  // because payment_date is the SLA clock. Tiebreak on createdAt DESC for stable
  // ordering when two payments share a timestamp.
  [`${LostStatuses.InProcess}-${ReportType.Lost}`]: [{ "lost.isUrgent": "DESC" }, { "payment_agg.payment_date": "ASC" }, { "lost.createdAt": "DESC" }],
  [`${LostStatuses.LFNotified}-${ReportType.Lost}`]: [{ "lost.statusUpdatedAt": "DESC" }, { "lost.createdAt": "DESC" }],
  [`${LostStatuses.DraftIncomplete}-${ReportType.Lost}`]: [{ "lost.createdAt": "DESC" }],
  [`${LostStatuses.DraftCompleted}-${ReportType.Lost}`]: [{ "lost.createdAt": "DESC" }],
  [`${LostStatuses.NotPaid}-${ReportType.Lost}`]: [{ "lost.createdAt": "DESC" }],
  [`${LostStatuses.Overdue}-${ReportType.Lost}`]: [{ "lost.statusUpdatedAt": "DESC" }, { "lost.createdAt": "DESC" }],
  [`${LostStatuses.Deleted}-${ReportType.Lost}`]: [{ "lost.statusUpdatedAt": "DESC" }, { "lost.createdAt": "DESC" }],
};
